/**
 * Brand Theme Registry
 *
 * Manages customer-specific brand themes for white-labeling.
 * Each brand is a CSS file that overrides accent/brand colors
 * via [data-brand="slug"] selectors on <html>.
 *
 * Usage:
 *   applyBrand('caspeco')   // sets data-brand="caspeco" on <html>
 *   removeBrand()           // removes data-brand, reverts to default
 *   getBrandMeta('caspeco') // returns display name, colors, logo URL
 */

export type BrandMeta = {
  slug: string;
  name: string;
  website: string;
  accent: string; // primary brand color (for previews / gradient)
  accentEnd?: string; // gradient end color (defaults to accent darkened)
  secondary?: string; // secondary brand color
  logoUrl?: string; // horizontal logo (white on transparent — use CSS filter for light bg)
  iconUrl?: string; // square icon for sidebar/favicon
  tagline?: string; // shown below logo on login page
  logoIsName?: boolean; // if true, the logo replaces both icon + name text in sidebar
};

/**
 * Registry of available brand themes.
 * Each entry maps to a CSS file in styles/theme/brands/{slug}.css
 */
export const BRAND_REGISTRY: Record<string, BrandMeta> = {
  caspeco: {
    slug: 'caspeco',
    name: 'Caspeco',
    website: 'https://caspeco.com',
    accent: '#1f8690',
    accentEnd: '#155f66',
    secondary: '#ffc120',
    logoUrl: '/brands/caspeco/logo.png',
    iconUrl: '/brands/caspeco/icon.png',
    tagline: 'Restaurant Intelligence Platform',
  },
  olearys: {
    slug: 'olearys',
    name: "O'Learys",
    website: 'https://olearys.com',
    accent: '#2d9b87',
    accentEnd: '#23695a',
    secondary: '#ffdc1e',
    logoUrl: '/brands/olearys/icon.svg',
    iconUrl: '/brands/olearys/icon.svg',
    tagline: 'Sports Bar & Restaurant',
    logoIsName: true,
  },
};

/** Apply a brand theme to the DOM */
export function applyBrand(slug: string): void {
  if (!BRAND_REGISTRY[slug]) {
    return;
  }
  document.documentElement.setAttribute('data-brand', slug);
}

/** Remove brand theme, revert to default Solidafy branding */
export function removeBrand(): void {
  document.documentElement.removeAttribute('data-brand');
}

/** Get the currently active brand slug (if any) */
export function getActiveBrand(): string | null {
  return document.documentElement.getAttribute('data-brand');
}

/** Get brand metadata by slug */
export function getBrandMeta(slug: string): BrandMeta | null {
  return BRAND_REGISTRY[slug] ?? null;
}

/** List all available brand slugs */
export function listBrands(): BrandMeta[] {
  return Object.values(BRAND_REGISTRY);
}
